/**
 * AST Publishing Modal
 * Confirms a publish and runs the flow for AST content.
 *
 * Two target kinds:
 *  - `whole`   — publish a whole source file (no `^` markers).
 *  - `section` — publish a single `^`-marked chapter section; updates the
 *                source's `publishedSections` + `encryptedSections`.
 *
 * Encryption keys are read from the book when the dialog opens, not from a prop:
 * a sentence typed while marking a passage is saved straight to the server, so a
 * snapshot taken at page load would be missing exactly the passage being published.
 */

import { Publish } from '@mui/icons-material'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'

import { apiService } from '../../services/api'
import { EncryptionKeys, PublishResult, publishChapter, publishChapterSection } from '../../services/publishService'
import { ContentAST } from '../../transforms/ast'
import { groupMarkedSectionsFromAST } from '../../utils/astContentMarking'
import { logger } from '../../utils/logger'

export type PublishTarget =
  | { kind: 'whole'; filename: string; authorContent: ContentAST }
  | {
      kind: 'section'
      sourceFilename: string
      headingBlockId: string
      sourceAuthorAST: ContentAST
      sliceAuthorAST: ContentAST
    }

interface PublishingModalProps {
  open: boolean
  onClose: () => void
  bookId: string
  target: PublishTarget
  onPublishSuccess?: () => void
}

export const PublishingModal: React.FC<PublishingModalProps> = ({
  open,
  onClose,
  bookId,
  target,
  onPublishSuccess,
}) => {
  const [publishing, setPublishing] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const [keys, setKeys] = useState<EncryptionKeys | null>(null)

  const astForMarkedCount = target.kind === 'whole' ? target.authorContent : target.sliceAuthorAST
  const grouped = useMemo(() => groupMarkedSectionsFromAST(astForMarkedCount), [astForMarkedCount])
  const hasMarkedContent = grouped.length > 0

  useEffect(() => {
    if (!open) {
      return
    }
    setError('')
    setDone(false)
    setKeys(null)

    let cancelled = false
    apiService
      .getBook(bookId)
      .then((res) => {
        if (cancelled) {
          return
        }
        setKeys({
          lockPhrases: res.data?.settings?.lockPhrases,
          withheldGlyphKey: res.data?.settings?.withheldGlyphKey,
        })
      })
      .catch((err) => {
        if (cancelled) {
          return
        }
        logger.error('Failed to load encryption keys before publishing:', err)
        setError('Could not load this book’s encryption keys')
      })

    return () => {
      cancelled = true
    }
  }, [open, bookId])

  const handlePublish = async () => {
    setPublishing(true)
    setError('')
    try {
      const encryptionKeys = keys ?? {}
      let result: PublishResult
      if (target.kind === 'whole') {
        result = await publishChapter(bookId, target.filename, target.authorContent, encryptionKeys)
      } else {
        result = await publishChapterSection(
          bookId,
          target.sourceFilename,
          target.headingBlockId,
          target.sourceAuthorAST,
          encryptionKeys,
        )
      }
      if (!result.success) {
        setError(result.message || 'Publishing failed')
        return
      }
      setDone(true)
      onPublishSuccess?.()
    } catch (e: any) {
      setError(e?.message || 'Publishing failed')
    } finally {
      setPublishing(false)
    }
  }

  const dialogTitle = target.kind === 'whole' ? 'Publish Chapter' : 'Publish Chapter Section'
  const waitingForKeys = hasMarkedContent && !keys

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <Publish />
          <Typography variant="h6">{dialogTitle}</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {hasMarkedContent && (
            <Alert severity="info">
              {grouped.length} marked passage{grouped.length === 1 ? '' : 's'} will be encrypted — sentence-locked ones
              with their own sentence, character ones with this book’s character key. Readers see glyphs.
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {done && <Alert severity="success">Published successfully.</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={publishing}>
          {done ? 'Close' : 'Cancel'}
        </Button>
        <Button
          onClick={handlePublish}
          variant="contained"
          disabled={publishing || done || waitingForKeys}
          startIcon={publishing ? <CircularProgress size={16} /> : <Publish />}
        >
          {publishing ? 'Publishing...' : 'Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
